/**
 * Pay-what-you-want amounts are handled in integer minor units (cents, or whole
 * yen when the currency takes no decimals) so comparisons never touch floats.
 * The backend stays the pricing authority: this only pre-checks the merchant's
 * own limits and the currency's decimals.
 */

export type AmountParseError = 'empty' | 'invalid' | 'wholeAmount' | 'decimals';

export type ParsedAmount = { minor: number } | { error: AmountParseError };

export type AmountValidationError = 'invalid' | 'belowMinimum' | 'aboveMaximum';

export interface AmountLimits {
    decimals: number;
    minimum: number | null;
    maximum: number | null;
}

export interface AmountFormat {
    prefix: string;
    suffix: string;
    decimalSeparator: string;
    groupSeparator: string;
}

const AMOUNT_INPUT = /^(\d+)(?:([.,])(\d*))?$/;

export const toMinorUnits = (value: number, decimals: number) =>
    Math.round(value * 10 ** decimals);

export const fromMinorUnits = (minor: number, decimals: number) =>
    minor / 10 ** decimals;

/**
 * Reads what the shopper typed: digits and at most one decimal separator,
 * never a thousands separator. The currency's own separator always reads as
 * decimal. The other one does only when at most `decimals` digits follow it,
 * so in USD "12,50" is 12.50 and "12,500" is refused, not read as 12,500.
 */
export function parseAmountInput(
    text: string,
    decimals: number,
    decimalSeparator = '.'
): ParsedAmount {
    const value = text.trim();
    if (!value) {
        return { error: 'empty' };
    }
    const match = value.match(AMOUNT_INPUT);
    if (!match) {
        return { error: 'invalid' };
    }

    const [, integer, separator, fraction = ''] = match;
    if (fraction.length > decimals) {
        if (separator !== decimalSeparator) {
            return { error: 'invalid' };
        }
        return { error: decimals === 0 ? 'wholeAmount' : 'decimals' };
    }

    const minor =
        Number(integer) * 10 ** decimals +
        Number(fraction.padEnd(decimals, '0') || '0');
    if (!Number.isSafeInteger(minor)) {
        return { error: 'invalid' };
    }
    return { minor };
}

export function validateAmount(
    minor: number,
    { decimals, minimum, maximum }: AmountLimits
): AmountValidationError | null {
    if (minor <= 0) {
        return 'invalid';
    }
    if (minimum !== null && minor < toMinorUnits(minimum, decimals)) {
        return 'belowMinimum';
    }
    if (maximum !== null && minor > toMinorUnits(maximum, decimals)) {
        return 'aboveMaximum';
    }
    return null;
}

const DEFAULT_FORMAT: AmountFormat = {
    prefix: '',
    suffix: '',
    decimalSeparator: '.',
    groupSeparator: ',',
};

const NUMBER_IN_TEMPLATE = /\d(?:[\d.,'\s]*\d)?/;
const TRAILING_DECIMALS = /([.,])\d{1,2}$/;
const GROUPING = /\d([.,'\s])\d{3}(?!\d)/;

const readTemplate = (template: string) => {
    const match = template.match(NUMBER_IN_TEMPLATE);
    if (!match || match.index === undefined) {
        return null;
    }
    const number = match[0];
    const decimalSeparator = number.match(TRAILING_DECIMALS)?.[1];
    const integerPart = decimalSeparator
        ? number.slice(0, number.lastIndexOf(decimalSeparator))
        : number;
    return {
        prefix: template.slice(0, match.index),
        suffix: template.slice(match.index + number.length),
        decimalSeparator,
        groupSeparator: integerPart.match(GROUPING)?.[1],
    };
};

/**
 * Learns the currency format from strings the server already formatted for
 * this product (`displayed_price`, suggested amounts, limits), so a typed
 * amount reads exactly like the prices next to it. The server picks a format
 * per currency (e.g. "10,50 €"), not per store locale, so neither `Intl` nor
 * the SDK's `formatMoney` would match it.
 */
export function parseAmountFormat(
    templates: Array<string | null | undefined>
): AmountFormat {
    const read = templates
        .filter((template): template is string => !!template)
        .map(readTemplate)
        .filter(
            (
                template
            ): template is NonNullable<ReturnType<typeof readTemplate>> =>
                template !== null
        );
    const best =
        read.find((template) => template.decimalSeparator) ??
        read.find((template) => template.groupSeparator) ??
        read[0];
    if (!best) {
        return DEFAULT_FORMAT;
    }

    // Every server format pairs "." with "," the other way round.
    const decimalSeparator =
        best.decimalSeparator ?? (best.groupSeparator === '.' ? ',' : '.');
    const groupSeparator =
        best.groupSeparator ?? (decimalSeparator === ',' ? '.' : ',');
    return {
        prefix: best.prefix,
        suffix: best.suffix,
        decimalSeparator,
        groupSeparator,
    };
}

/**
 * "$1,234.56" with the format, or with `forInput` what the field shows after a
 * suggestion is picked: no symbol, no grouping, no fraction on a whole amount
 * ("1234.56", "100").
 */
export function formatMinorAmount(
    minor: number,
    decimals: number,
    format: AmountFormat,
    { forInput = false } = {}
): string {
    const factor = 10 ** decimals;
    const fraction = minor % factor;
    let number = String(Math.floor(minor / factor));
    if (!forInput) {
        number = number.replace(/\B(?=(\d{3})+(?!\d))/g, format.groupSeparator);
    }
    if (decimals > 0 && (fraction || !forInput)) {
        number += `${format.decimalSeparator}${String(fraction).padStart(
            decimals,
            '0'
        )}`;
    }
    return forInput ? number : `${format.prefix}${number}${format.suffix}`;
}

export const getCurrencySymbol = (format: AmountFormat, currency: string) =>
    (format.prefix || format.suffix).trim() || currency;

const roundHalfEven = (value: number) => {
    const floor = Math.floor(value);
    if (Math.abs(value - floor - 0.5) < 1e-9) {
        return floor % 2 === 0 ? floor : floor + 1;
    }
    return Math.round(value);
};

/** Matches the backend, which banker's-rounds amount + surcharge to the currency's decimals. */
export const addSurchargeToMinor = (
    minor: number,
    surcharge: number,
    decimals: number
) => roundHalfEven(minor + surcharge * 10 ** decimals);
